import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterOutlet } from '@angular/router';

import { AutomationModule } from '@frontend/sports/automation/core-feature';
import { ScrollAdapterComponent } from '@frontend/sports/common/core/feature/scroll-adapter';
import { ToCssUrlPipe } from '@frontend/sports/common/core/utils/css-formatters';
import { TrustPipe } from '@frontend/sports/common/core/utils/dom';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { CountdownTimerComponent } from '@frontend/sports/common/ui/countdown-timer';
import { WhenOverflowedDirective } from '@frontend/sports/common/ui/overflow';
import { ListItemComponent } from '@frontend/sports/common/ui/sports-list-items';
import { TextAutoScrollDirective } from '@frontend/sports/common/ui/text-auto-scroll';
import { ItemComponent, ItemTreeComponent } from '@frontend/sports/item/feature';
import { ExternalPriceFormatterPipe, NativePriceFormatterPipe } from '@frontend/sports/odds/feature/native-price';
import { TrackReferringACtionDirective } from '@frontend/sports/tracking/feature';
import { DsButton } from '@frontend/ui/button';
import { DsButtonIcon } from '@frontend/ui/button-icon';
import { DsDivider } from '@frontend/ui/divider';
import { DsListItem } from '@frontend/ui/list-item';
import { DsNotificationBubble } from '@frontend/ui/notification-bubble';
import { DsPill } from '@frontend/ui/pill';
import { DsTabsGroup, DsTabsModule } from '@frontend/ui/tabsgroup';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { BannerComponent } from '../banner/banner.component';
import { BestOddsCounterComponent } from '../best-odds-guarantee/best-odds-counter.component';
import { RaceViewBestOddsBadgeComponent } from '../best-odds-guarantee/race-view-best-odds-badge.component';
import { TranslationToStringPipe } from '../cds/translation.pipe';
import { ErrorComponent } from '../common/error/error.component';
import { CrmPromotionContainerComponent } from '../crm-promotion-container/crm-promotion-container.component';
import { BadgeComponent } from '../crm-promotion/badge.component';
import { CrmPromotionModule } from '../crm-promotion/crm-promotion.module';
import { DirectivesModule } from '../directives/directives.module';
import { DropdownModule } from '../dropdown/dropdown.module';
import { HorsesFixtureParticipantHelper } from '../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixtureSortingHelper } from '../horse-racing-shared/services/horses-fixture-sorting-helper.service';
import { NativeAlertsModule } from '../native-alerts/native-alerts.module';
import { NavigationCoreModule } from '../navigation-core/navigation-core.module';
import { MarketHelpComponent } from '../option-group/market-help/market-help.component';
import { FixturePickComponent } from '../option-pick/fixture-pick.component';
import { FreeFormPickComponent } from '../option-pick/free-form-pick.component';
import { HrOptionPickComponent } from '../option-pick/hr-option-pick.component';
import { RaceXCastPickComponent } from '../option-pick/race-xcast-pick.component';
import { TopBottomSectionLinksComponent } from '../quick-links/top-bottom-section-links/top-bottom-section-links.component';
import { FixtureUpdateService } from './common/fixture-update.service';
import { HorsesBaseComponent } from './common/horses-base.component';
import { ParticipantsService } from './common/participant-service';
import { RacingFixtureContainerComponent } from './common/racing-fixture-container.component';
import { AntepostFixtureListBreadcrumbsResolver } from './components/antepost-fixture-list/antepost-fixture-breadcrumb.resolver';
import { AntepostFixtureListResolver } from './components/antepost-fixture-list/antepost-fixture-list.resolver';
import { AntepostCommonComponent } from './components/antepost-fixture/antepost-common.component';
import { AntepostContainerComponent } from './components/antepost-fixture/antepost-container.component';
import { AntepostFixtureBreadcrumbsResolver } from './components/antepost-fixture/antepost-fixture-breadcrumb.resolver';
import { AntepostFixtureComponent } from './components/antepost-fixture/antepost-fixture.component';
import { AntepostFixtureResolver } from './components/antepost-fixture/antepost-fixture.resolver';
import { AntepostMarketComponent } from './components/antepost-fixture/antepost-market.component';
import { AntepostOptionMarketComponent } from './components/antepost-fixture/antepost-option-market.component';
import { AntepostParticipantsComponent } from './components/antepost-fixture/antepost-participants.component';
import { HorsesRaceSubNavigationService } from './components/base/horses-race-sub-navigation.service';
import { HorsesSubNavigationService } from './components/base/horses-sub-navigation.service';
import { FixtureMarketTitleComponent } from './components/fixture-market-title/fixture-market-title.component';
import { GreyHoundsBaseComponent } from './components/greyhounds/base/greyhounds-base.component';
import { GreyhoundSubNavigationService } from './components/greyhounds/base/greyhounds-sub-navigation.service';
import { GreyhoundHomeComponent } from './components/greyhounds/gh-home/greyhound-home.component';
import { GreyhoundHomeResolver } from './components/greyhounds/gh-home/greyhound-home.resolver';
import { GreyhoundsMeetingSwitcherComponent } from './components/greyhounds/greyhounds-meeting-switcher/greyhounds-meeting-switcher.component';
import { GreyhoundsRaceResultedComponent } from './components/greyhounds/greyhounds-race-result/greyhounds-race-resulted.component';
import { GreyhoundsResultedPlacedParticipantsComponent } from './components/greyhounds/greyhounds-race-result/greyhounds-resulted-placed-participants.component';
import { ResultedGreyhoundsNonRunnerParticipantsComponent } from './components/greyhounds/greyhounds-race-result/resulted-greyhounds-non-runner-participants.component';
import { GreyhoundsDerivedRaceComponent } from './components/greyhounds/greyhounds-race/greyhounds-derived-race.component';
import { GreyhoundsRaceComponent } from './components/greyhounds/greyhounds-race/greyhounds-race.component';
import { RacingpostPickComponent } from './components/greyhounds/greyhounds-racingpost-pick/racingpost-pick.component';
import { GreyhoundsTodayResolver } from './components/greyhounds/greyhounds-today/greyhounds-today.resolver';
import { MeetingSwitcherComponent } from './components/greyhounds/meeting-switcher/meeting-switcher.component';
import { GhNextRacesComponent } from './components/greyhounds/next-races/gh-next-races.component';
import { GhRaceDistanceComponent } from './components/greyhounds/race-distance/gh-race-distance.component';
import { GhRaceInfoComponent } from './components/greyhounds/race-info/gh-race-info.component';
import { GreyhoundTimerComponent } from './components/greyhounds/race-info/greyhound-timer.component';
import { GhDerivedWinMarketParticipantsComponent } from './components/greyhounds/win-market-participants/gh-derived-win-market-participants.component';
import { GhTrapImagesComponent } from './components/greyhounds/win-market-participants/gh-trap-images.component';
import { GhWinMarketParticipantsComponent } from './components/greyhounds/win-market-participants/gh-win-market-participants.component';
import { HorseInfoDescriptionComponent } from './components/horse-info/horse-info-description.component';
import { HorseInfoHeaderComponent } from './components/horse-info/horse-info-header.component';
import { HorseInfoComponent } from './components/horse-info/horse-info.component';
import { HorsesForecastComponent } from './components/horses-forecast/horses-forecast.component';
import { MeetingComponent } from './components/horses-meeting/meeting.component';
import { HorsesRaceBadgesComponent } from './components/horses-race-badges/horses-race-badges.component';
import { HorsesRaceResultedComponent } from './components/horses-race-resulted/horses-race-resulted.component';
import { ResultedNonRunnerParticipantsComponent } from './components/horses-race-resulted/resulted-non-runner-participants.component';
import { ResultedPlacedParticipantsComponent } from './components/horses-race-resulted/resulted-placed-participants.component';
import { TimeformTipComponent } from './components/horses-race-timeform-tip/timeform-tip.component';
import { HorsesRaceWinMarketsortComponent } from './components/horses-race-win-market-sort/horses-race-win-market-sort.component';
import { HorsesDerivedRaceComponent } from './components/horses-race/horses-derived-race.component';
import { HorsesRaceComponent } from './components/horses-race/horses-race.component';
import { HorsesSpecialsComponent } from './components/horses-specials/horses-specials.component';
import { TimeFormPickComponent } from './components/horses-timeform-pick/timeform-pick.component';
import { HorsesTodayResolver } from './components/horses-today/horses-today.resolver';
import { HorsesTomorrowResolver } from './components/horses-tomorrow/horses-tomorrow.resolver';
import { HorsesDerivedRaceWinMarketComponent } from './components/horses-win-market/horses-derived-race-win-market.component';
import { HorsesRaceWinMarketComponent } from './components/horses-win-market/horses-race-win-market.component';
import { NextRacesCardPreviewComponent } from './components/next-races-card-preview/next-races-card-preview.component';
import { RaceTimeStatusComponent } from './components/next-races-card-preview/race-time-status.component';
import { NextRacesTitlePreviewComponent } from './components/next-races-title-preview/next-races-title-preview.component';
import { NextRacesComponent } from './components/next-races/next-races.component';
import { DerivedRacePreviewComponent } from './components/race-preview/derived-race-preview.component';
import { HorsesSilkComponent } from './components/race-preview/horses-silk.component';
import { RacePreviewComponent } from './components/race-preview/race-preview.component';
import { RaceTimeformSpotlightComponent } from './components/race-timeform-spotlight/race-timeform-spotlight.component';
import { RacingPromoBannerComponent } from './components/racing-promo-banner/racing-promo-banner.component';
import { StarRatingComponent } from './components/star-rating/star-rating.component';
import { DayOfRaceLobbyComponent } from './components/tournaments/day-of-race-lobby.component';
import { DayOfRaceMeetingListComponent } from './components/tournaments/day-of-race-meeting-list.component';
import { HorsesTournamentSelectorComponent } from './components/tournaments/horses-tournament-selector.component';
import { RacingStylesResolver } from './racing-styles.resolver';
import { CrmPromotionMapperService } from './services/crm-promotion-mapper.service';
import { GreyhoundsBreadcrumbResolver } from './services/greyhounds-breadcrumbs.resolver';
import { HorsesBreadcrumbResolver } from './services/horses-breadcrumbs.resolver';
import { HorsesRaceService } from './services/horses-race.service';
import { ResultedDividentsCalculatorService } from './services/resulted-dividents-calculator.service';

const components = [
    AntepostFixtureComponent,
    AntepostMarketComponent,
    AntepostOptionMarketComponent,
    AntepostParticipantsComponent,
    AntepostCommonComponent,
    AntepostContainerComponent,
    DayOfRaceLobbyComponent,
    DayOfRaceMeetingListComponent,
    FixtureMarketTitleComponent,
    HorseInfoComponent,
    HorseInfoDescriptionComponent,
    HorseInfoHeaderComponent,
    HorsesBaseComponent,
    GreyHoundsBaseComponent,
    HorsesForecastComponent,
    HorsesRaceBadgesComponent,
    HorsesRaceComponent,
    HorsesRaceResultedComponent,
    HorsesRaceWinMarketComponent,
    HorsesRaceWinMarketsortComponent,
    HorsesSilkComponent,
    HorsesTournamentSelectorComponent,
    NextRacesComponent,
    RacePreviewComponent,
    RaceTimeformSpotlightComponent,
    ResultedNonRunnerParticipantsComponent,
    ResultedPlacedParticipantsComponent,
    StarRatingComponent,
    TimeformTipComponent,
    NextRacesCardPreviewComponent,
    RaceTimeStatusComponent,
    NextRacesTitlePreviewComponent,
    GreyhoundHomeComponent,
    GhNextRacesComponent,
    GhWinMarketParticipantsComponent,
    RacingFixtureContainerComponent,
    GhRaceInfoComponent,
    GhRaceDistanceComponent,
    GreyhoundTimerComponent,
    GreyhoundsRaceComponent,
    GreyhoundsRaceResultedComponent,
    GreyhoundsResultedPlacedParticipantsComponent,
    ResultedGreyhoundsNonRunnerParticipantsComponent,
    GreyhoundsMeetingSwitcherComponent,
    RacingPromoBannerComponent,
    RacingpostPickComponent,
    TimeFormPickComponent,
    MeetingSwitcherComponent,
    HorsesDerivedRaceWinMarketComponent,
    HorsesDerivedRaceComponent,
    HorsesSpecialsComponent,
    DerivedRacePreviewComponent,
    MeetingComponent,
    GreyhoundsDerivedRaceComponent,
    GhDerivedWinMarketParticipantsComponent,
    GhTrapImagesComponent,
];

@NgModule({
    declarations: [...components],
    exports: [...components],
    providers: [
        AntepostFixtureBreadcrumbsResolver,
        AntepostFixtureListBreadcrumbsResolver,
        AntepostFixtureListResolver,
        AntepostFixtureResolver,
        CrmPromotionMapperService,
        HorsesBreadcrumbResolver,
        GreyhoundsBreadcrumbResolver,
        HorsesFixtureParticipantHelper,
        HorsesFixtureSortingHelper,
        HorsesRaceService,
        HorsesRaceSubNavigationService,
        HorsesSubNavigationService,
        GreyhoundSubNavigationService,
        HorsesTodayResolver,
        HorsesTomorrowResolver,
        RacingStylesResolver,
        ResultedDividentsCalculatorService,
        GreyhoundsTodayResolver,
        GreyhoundHomeResolver,
        ParticipantsService,
        FixtureUpdateService,
    ],
    imports: [
        AutomationModule,
        BannerComponent,
        RaceViewBestOddsBadgeComponent,
        BestOddsCounterComponent,
        TranslationToStringPipe,
        CommonModule,
        CrmPromotionContainerComponent,
        CrmPromotionModule,
        DirectivesModule,
        FormsModule,
        NativeAlertsModule,
        NavigationCoreModule,
        DropdownModule,
        TopBottomSectionLinksComponent,
        ErrorComponent,
        ItemComponent,
        TextAutoScrollDirective,
        StringInterpolationPipe,
        TrustPipe,
        CountdownTimerComponent,
        ScrollAdapterComponent,
        ItemTreeComponent,
        WhenOverflowedDirective,
        BadgeComponent,
        FixturePickComponent,
        HrOptionPickComponent,
        RaceXCastPickComponent,
        FreeFormPickComponent,
        MarketHelpComponent,
        DsButton,
        DsPill,
        RouterOutlet,
        IconCustomComponent,
        DsButtonIcon,
        DsTabsGroup,
        DsTabsModule,
        DsDivider,
        DsListItem,
        IconCustomComponent,
        DsNotificationBubble,
        ListItemComponent,
        ExternalPriceFormatterPipe,
        NativePriceFormatterPipe,
        ToCssUrlPipe,
        TrackReferringACtionDirective,
        NgOptimizedImage,
    ],
})
export class RacingCommonModule {
    static forChild(): ModuleWithProviders<RacingCommonModule> {
        return {
            ngModule: RacingCommonModule,
            providers: [
                AntepostFixtureBreadcrumbsResolver,
                AntepostFixtureListBreadcrumbsResolver,
                AntepostFixtureListResolver,
                AntepostFixtureResolver,
                CrmPromotionMapperService,
                HorsesBreadcrumbResolver,
                GreyhoundsBreadcrumbResolver,
                HorsesFixtureParticipantHelper,
                HorsesFixtureSortingHelper,
                HorsesRaceService,
                HorsesRaceSubNavigationService,
                HorsesSubNavigationService,
                HorsesTodayResolver,
                HorsesTomorrowResolver,
                RacingStylesResolver,
                ResultedDividentsCalculatorService,
                GreyhoundsTodayResolver,
                ParticipantsService,
                FixtureUpdateService,
            ],
        };
    }
}
